import { ChevronLeft, ChevronRight, Close, Event } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  IconButton,
  Stack,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import "dayjs/locale/pt-br";
import {
  getMobileSheetSlideProps,
  getMobileSheetTransitionDuration,
} from "@/components/common/mobileSheetMotion";

export interface FitnessModalDateRowProps {
  selectedDate: Dayjs;
  /** Último dia permitido (ex.: hoje). */
  today: Dayjs;
  loading: boolean;
  onPrevDay: () => void;
  onNextDay: () => void;
  /** Data escolhida no calendário (o pai aplica limite até `today`). */
  onPickDate: (date: Dayjs) => void;
  /** Ocupa 100% da largura do pai (ex.: linha com botão de busca ao lado). */
  fullWidth?: boolean;
  /**
   * Modo compacto: setas e campo de data menores (ex.: painel estreito no atendimento).
   */
  compact?: boolean;
  /**
   * Largura só do conteúdo, alinhado ao início (ex.: faixa verde não ocupa a tela toda).
   */
  inline?: boolean;
  /**
   * `primary`: texto branco sobre faixa verde (modal padrão).
   * `surface`: sobre fundo claro (seção embutida no dashboard mobile).
   */
  tone?: "primary" | "surface";
}

/**
 * Faixa de data fitness: DatePicker original no desktop; bottom sheet no mobile.
 */
export function FitnessModalDateRow({
  selectedDate,
  today,
  loading,
  onPrevDay,
  onNextDay,
  onPickDate,
  fullWidth = false,
  compact = false,
  inline = false,
  tone = "primary",
}: FitnessModalDateRowProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(selectedDate);
  const onSurface = tone === "surface";

  useEffect(() => {
    if (drawerOpen) setDraftDate(selectedDate);
  }, [drawerOpen, selectedDate]);

  const navButtonSx = onSurface
    ? {
        color: "primary800.main",
        bgcolor: (t: { palette: { primary100: { main: string } } }) =>
          alpha(t.palette.primary100.main, 0.65),
        "&:hover": {
          bgcolor: (t: { palette: { primary100: { main: string } } }) =>
            alpha(t.palette.primary100.main, 1),
        },
        "&.Mui-disabled": {
          color: "text.disabled",
          bgcolor: (t: { palette: { neutral100: { main: string } } }) =>
            alpha(t.palette.neutral100.main, 0.8),
        },
      }
    : {
        color: "white",
        bgcolor: (t: { palette: { neutral0: { main: string } } }) =>
          alpha(t.palette.neutral0.main, 0.12),
        "&:hover": {
          bgcolor: (t: { palette: { neutral0: { main: string } } }) =>
            alpha(t.palette.neutral0.main, 0.2),
        },
        "&.Mui-disabled": {
          color: (t: { palette: { neutral0: { main: string } } }) =>
            alpha(t.palette.neutral0.main, 0.3),
          bgcolor: (t: { palette: { neutral0: { main: string } } }) =>
            alpha(t.palette.neutral0.main, 0.06),
        },
      };

  const datePickerSx = {
    flex: compact ? "0 1 auto" : "1 1 auto",
    minWidth: 0,
    maxWidth: compact ? 228 : undefined,
    "& .MuiInputBase-root": {
      color: onSurface ? "text.primary" : "white",
      bgcolor: onSurface
        ? (t: { palette: { primary100: { main: string } } }) =>
            alpha(t.palette.primary100.main, 0.45)
        : (t: { palette: { neutral0: { main: string } } }) =>
            alpha(t.palette.neutral0.main, 0.12),
      borderRadius: 2,
      minHeight: compact ? 36 : undefined,
      "&:hover": {
        bgcolor: onSurface
          ? (t: { palette: { primary100: { main: string } } }) =>
              alpha(t.palette.primary100.main, 0.75)
          : (t: { palette: { neutral0: { main: string } } }) =>
              alpha(t.palette.neutral0.main, 0.2),
      },
      "& fieldset": {
        border: onSurface ? undefined : "none",
        borderColor: onSurface ? "divider" : undefined,
      },
    },
    "& .MuiInputBase-input": {
      textAlign: "center",
      fontWeight: 600,
      py: compact ? 0.5 : 1,
      px: compact ? 0.5 : { xs: 1, sm: 2 },
      fontSize: compact ? "0.75rem" : { xs: "0.8rem", sm: "1rem" },
      lineHeight: 1.35,
      cursor: loading ? "default" : "pointer",
    },
    "& .MuiSvgIcon-root": {
      color: onSurface ? "primary800.main" : "white",
    },
  };

  const handleOpenDatePicker = () => {
    if (loading) return;
    if (isMobile) {
      setDrawerOpen(true);
      return;
    }
    setDatePickerOpen(true);
  };

  const handleConfirmDrawerDate = () => {
    const clamped = draftDate.isAfter(today, "day")
      ? today.startOf("day")
      : draftDate.startOf("day");
    onPickDate(clamped);
    setDrawerOpen(false);
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={compact ? 0.65 : 1}
        alignItems="center"
        justifyContent={inline ? "flex-start" : "center"}
        sx={{
          width: fullWidth
            ? "100%"
            : inline
              ? "auto"
              : { xs: "100%", sm: "auto" },
          minWidth: 0,
        }}
      >
        <IconButton
          size="small"
          disabled={loading}
          onClick={onPrevDay}
          aria-label="Dia anterior"
          sx={{ flexShrink: 0, p: compact ? 0.5 : undefined, ...navButtonSx }}
        >
          <ChevronLeft sx={{ fontSize: compact ? 20 : 24 }} />
        </IconButton>

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <DatePicker
            open={isMobile ? false : datePickerOpen}
            onClose={() => setDatePickerOpen(false)}
            value={selectedDate}
            maxDate={today}
            disabled={loading}
            onChange={(newDate) => {
              if (newDate) {
                onPickDate(newDate);
                setDatePickerOpen(false);
              }
            }}
            format={compact ? "DD/MM/YYYY" : "DD [de] MMMM [de] YYYY"}
            sx={datePickerSx}
            slotProps={{
              textField: {
                variant: "outlined",
                InputProps: {
                  readOnly: true,
                  startAdornment: (
                    <Event
                      sx={{
                        color: onSurface ? "primary800.main" : "white",
                        mr: compact ? 0.35 : 0.5,
                        fontSize: compact ? 16 : 18,
                      }}
                    />
                  ),
                  endAdornment: <></>,
                },
                onClick: handleOpenDatePicker,
              },
              openPickerIcon: {
                sx: { display: "none" },
              },
              popper: {
                sx: {
                  "& .MuiPaper-root": {
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
                    borderRadius: 2,
                  },
                },
              },
            }}
          />
        </LocalizationProvider>

        <IconButton
          size="small"
          disabled={loading || selectedDate.isSame(today, "day")}
          onClick={onNextDay}
          aria-label="Próximo dia"
          sx={{ flexShrink: 0, p: compact ? 0.5 : undefined, ...navButtonSx }}
        >
          <ChevronRight sx={{ fontSize: compact ? 20 : 24 }} />
        </IconButton>
      </Stack>

      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => undefined}
          disableSwipeToOpen
          swipeAreaWidth={0}
          transitionDuration={getMobileSheetTransitionDuration()}
          SlideProps={getMobileSheetSlideProps(theme)}
          ModalProps={{
            keepMounted: false,
            sx: { zIndex: theme.zIndex.modal + 4 },
            BackdropProps: {
              sx: { bgcolor: alpha(theme.palette.common.black, 0.5) },
              transitionDuration: getMobileSheetTransitionDuration(),
            },
          }}
          PaperProps={{
            sx: {
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: "20px 20px 0 0",
              maxHeight: "88dvh",
              bgcolor: "background.paper",
              boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.18)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: 99,
                bgcolor: "divider",
              }}
            />
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2,
              pb: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Selecionar data
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Escolha o dia dos dados fitness
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label="Fechar"
              onClick={() => setDrawerOpen(false)}
              sx={{ flexShrink: 0 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              display: "flex",
              justifyContent: "center",
              px: 1,
              py: 0.5,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
              <StaticDatePicker
                displayStaticWrapperAs="mobile"
                value={draftDate}
                maxDate={today}
                onChange={(value) => {
                  if (value) setDraftDate(dayjs(value).startOf("day"));
                }}
                slotProps={{
                  toolbar: { hidden: true },
                  actionBar: { actions: [] },
                }}
                sx={{
                  width: "100%",
                  maxWidth: 360,
                  "& .MuiPickersCalendarHeader-root": {
                    px: 0.5,
                  },
                  "& .MuiDayCalendar-root": {
                    width: "100%",
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Stack
            direction="row"
            spacing={1.25}
            sx={{
              px: 2,
              pt: 1,
              pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setDrawerOpen(false)}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleConfirmDrawerDate}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Confirmar
            </Button>
          </Stack>
        </SwipeableDrawer>
      )}
    </>
  );
}
